import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import {
  dsrAuditsApi,
  DSRReport,
  SFTPRemoteFile,
  SFTPStatusResponse,
} from "../../../api/dsrAuditsApi";

const REPORT_TYPES: Array<{
  type: DSRReport["reportType"];
  label: string;
}> = [
  { type: "genent", label: "GenEnt" },
  { type: "music-videos", label: "Music Videos" },
  { type: "sound-recordings", label: "Sound Recordings" },
];

const HISTORY_LIMIT = 15;

const DSR_FILENAME_PATTERN =
  /^Moonbug_DSR_(GeneralEntertainment|MusicVideo|SoundRecording)_Mapping_\d{2}(?:January|February|March|April|May|June|July|August|September|October|November|December)\d{4}\.(xlsx|csv)$/i;

const formatBytes = (bytes: number): string => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (value?: string | number | null): string => {
  if (value == null || value === "") return "—";
  const date =
    typeof value === "number" ? new Date(value) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatAge = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "generated today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
};

const SftpBrowserRoute = () => {
  const toast = useRef<Toast>(null);
  const [status, setStatus] = useState<SFTPStatusResponse | null>(null);
  const [files, setFiles] = useState<SFTPRemoteFile[]>([]);
  const [availableByType, setAvailableByType] = useState<
    Partial<Record<DSRReport["reportType"], DSRReport[]>>
  >({});
  const [selectedReportIds, setSelectedReportIds] = useState<
    Partial<Record<DSRReport["reportType"], string>>
  >({});
  const [selectedTypes, setSelectedTypes] = useState<
    Set<DSRReport["reportType"]>
  >(
    () =>
      new Set<DSRReport["reportType"]>([
        "genent",
        "music-videos",
        "sound-recordings",
      ])
  );
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);
  const [uploadingReports, setUploadingReports] = useState(false);
  const [uploadingManual, setUploadingManual] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [pendingManualFile, setPendingManualFile] = useState<File | null>(null);

  const applyNewestOfEach = useCallback(
    (byType: Partial<Record<DSRReport["reportType"], DSRReport[]>>) => {
      const nextIds: Partial<Record<DSRReport["reportType"], string>> = {};
      const nextSelected = new Set<DSRReport["reportType"]>();
      for (const { type } of REPORT_TYPES) {
        const newest = byType[type]?.[0];
        if (newest) {
          nextIds[type] = newest.id;
          nextSelected.add(type);
        }
      }
      setSelectedReportIds(nextIds);
      setSelectedTypes(nextSelected);
    },
    []
  );

  const loadStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const response = await dsrAuditsApi.getSftpStatus();
      setStatus(response);
    } catch (error) {
      console.error("Error loading SFTP status:", error);
      setStatus({
        connected: false,
        host: "sftp.content.bmat.com",
        port: 22,
        username: "moonbug_dsr",
        cwd: "/",
        message: "Failed to reach SFTP status endpoint",
      });
      toast.current?.show({
        severity: "error",
        summary: "SFTP status",
        detail: "Could not check BMAT SFTP connection",
        life: 4000,
      });
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadFiles = useCallback(async () => {
    try {
      setLoadingFiles(true);
      const response = await dsrAuditsApi.listSftpFiles("/");
      setFiles(response.files || []);
    } catch (error) {
      console.error("Error listing SFTP files:", error);
      toast.current?.show({
        severity: "error",
        summary: "SFTP listing",
        detail: "Failed to list remote files",
        life: 4000,
      });
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  const loadAvailableReports = useCallback(async () => {
    try {
      setLoadingReports(true);
      const entries = await Promise.all(
        REPORT_TYPES.map(async ({ type }) => {
          const reports = await dsrAuditsApi.getReports(type);
          const available = (reports || [])
            .filter((r) => r.status === "available")
            .slice(0, HISTORY_LIMIT);
          return [type, available] as const;
        })
      );
      const next: Partial<Record<DSRReport["reportType"], DSRReport[]>> = {};
      for (const [type, reports] of entries) {
        next[type] = reports;
      }
      setAvailableByType(next);
      applyNewestOfEach(next);
    } catch (error) {
      console.error("Error loading available reports:", error);
      toast.current?.show({
        severity: "error",
        summary: "Reports",
        detail: "Failed to load DSR reports",
        life: 4000,
      });
    } finally {
      setLoadingReports(false);
    }
  }, [applyNewestOfEach]);

  useEffect(() => {
    void loadStatus();
    void loadFiles();
    void loadAvailableReports();
  }, [loadStatus, loadFiles, loadAvailableReports]);

  const toggleType = (type: DSRReport["reportType"], checked: boolean) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(type);
      else next.delete(type);
      return next;
    });
  };

  const getSelectedReport = (
    type: DSRReport["reportType"]
  ): DSRReport | undefined => {
    const id = selectedReportIds[type];
    const list = availableByType[type] || [];
    return list.find((r) => r.id === id) || list[0];
  };

  const handleUploadSelected = () => {
    const reportsToUpload = REPORT_TYPES.map(({ type }) => getSelectedReport(type))
      .filter((report): report is DSRReport => Boolean(report))
      .filter((report) => selectedTypes.has(report.reportType));

    if (reportsToUpload.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No reports selected",
        detail: "Select at least one available report to upload",
        life: 3000,
      });
      return;
    }

    confirmDialog({
      header: "QA accept and upload to BMAT",
      message: (
        <div>
          <p style={{ marginTop: 0 }}>
            I have QA-reviewed these reports and accept upload to BMAT SFTP:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: "1.25rem" }}>
            {reportsToUpload.map((r) => (
              <li key={r.id}>
                {r.fileName}
                <span style={{ color: "var(--text-color-secondary)" }}>
                  {" "}
                  ({formatAge(r.createdAt)})
                </span>
              </li>
            ))}
          </ul>
        </div>
      ),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Accept & Upload",
      rejectLabel: "Cancel",
      accept: async () => {
        try {
          setUploadingReports(true);
          const response = await dsrAuditsApi.uploadReportsToSftp(
            reportsToUpload.map((r) => r.id),
            true
          );
          const failed = response.results.filter((r) => !r.success);
          toast.current?.show({
            severity: failed.length ? "warn" : "success",
            summary: "SFTP upload",
            detail: response.message,
            life: 5000,
          });
          await Promise.all([loadFiles(), loadAvailableReports()]);
        } catch (error) {
          console.error("Error uploading reports to SFTP:", error);
          toast.current?.show({
            severity: "error",
            summary: "SFTP upload failed",
            detail:
              error instanceof Error
                ? error.message
                : "Failed to upload reports",
            life: 5000,
          });
        } finally {
          setUploadingReports(false);
        }
      },
    });
  };

  const getApiErrorDetail = (error: unknown): string => {
    const axiosError = error as {
      response?: { data?: { error?: string; errors?: string[] } };
      message?: string;
    };
    const data = axiosError?.response?.data;
    if (data?.errors?.length) return data.errors.join("; ");
    if (data?.error) return data.error;
    if (error instanceof Error) return error.message;
    return "Request failed";
  };

  const handleManualSelect = (event: FileUploadHandlerEvent) => {
    const file = event.files?.[0];
    event.options.clear();
    if (!file) return;

    if (!DSR_FILENAME_PATTERN.test(file.name)) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid filename",
        detail:
          "Name must be Moonbug_DSR_{GeneralEntertainment|MusicVideo|SoundRecording}_Mapping_{ddMonthyyyy}.xlsx (or .csv)",
        life: 7000,
      });
      return;
    }

    setPendingManualFile(file);
    confirmDialog({
      header: "Upload directly to BMAT SFTP?",
      message: (
        <div>
          <p style={{ marginTop: 0, fontWeight: 600 }}>
            This uploads immediately to sftp.content.bmat.com — there is no draft
            step.
          </p>
          <p>
            File: <code>{file.name}</code> ({formatBytes(file.size)})
          </p>
          <p style={{ color: "var(--text-color-secondary)" }}>
            The server will reject the upload if the filename or required DSR
            columns do not match the expected schema.
          </p>
        </div>
      ),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Upload to BMAT now",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => {
        void (async () => {
          try {
            setUploadingManual(true);
            const response = await dsrAuditsApi.uploadManualFileToSftp(file);
            toast.current?.show({
              severity: "success",
              summary: "Manual upload",
              detail: `Uploaded ${response.fileName} (${
                response.reportKind || "DSR"
              }) → ${response.remotePath}`,
              life: 5000,
            });
            setPendingManualFile(null);
            await loadFiles();
          } catch (error) {
            console.error("Error uploading manual file:", error);
            toast.current?.show({
              severity: "error",
              summary: "Manual upload failed",
              detail: getApiErrorDetail(error),
              life: 8000,
            });
          } finally {
            setUploadingManual(false);
          }
        })();
      },
      reject: () => setPendingManualFile(null),
    });
  };

  const handleDeleteRemoteFile = (row: SFTPRemoteFile) => {
    if (row.type !== "file") return;
    confirmDialog({
      header: "Delete from BMAT SFTP?",
      message: (
        <div>
          <p style={{ marginTop: 0, fontWeight: 600 }}>
            Permanently delete this file from the remote SFTP root?
          </p>
          <p>
            <code>{row.name}</code>
          </p>
        </div>
      ),
      icon: "pi pi-trash",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => {
        void (async () => {
          try {
            setDeletingFile(row.name);
            await dsrAuditsApi.deleteSftpFile(row.name);
            toast.current?.show({
              severity: "success",
              summary: "Deleted",
              detail: `Removed ${row.name} from BMAT SFTP`,
              life: 4000,
            });
            await loadFiles();
          } catch (error) {
            console.error("Error deleting SFTP file:", error);
            toast.current?.show({
              severity: "error",
              summary: "Delete failed",
              detail: getApiErrorDetail(error),
              life: 6000,
            });
          } finally {
            setDeletingFile(null);
          }
        })();
      },
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Card title="BMAT SFTP Connection">
        {loadingStatus ? (
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            <Tag
              severity={status?.connected ? "success" : "danger"}
              value={status?.connected ? "Connected" : "Disconnected"}
            />
            <span>
              <strong>{status?.username}</strong>@{status?.host}:
              {status?.port}
            </span>
            <span>cwd: {status?.cwd}</span>
            <span style={{ color: "var(--text-color-secondary)" }}>
              {status?.message}
            </span>
            <Button
              label="Refresh status"
              icon="pi pi-refresh"
              text
              onClick={() => void loadStatus()}
            />
          </div>
        )}
      </Card>

      <Card title="Upload reports to BMAT">
        <p style={{ marginTop: 0, color: "var(--text-color-secondary)" }}>
          Choose any available run per report type — not limited to today. Use
          &quot;Newest of each&quot; to select the most recent available GenEnt,
          Music Videos, and Sound Recordings files, then QA-accept before upload.
        </p>
        {loadingReports ? (
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div>
              <Button
                label="Newest of each"
                icon="pi pi-clock"
                outlined
                onClick={() => applyNewestOfEach(availableByType)}
                disabled={loadingReports}
              />
            </div>
            {REPORT_TYPES.map(({ type, label }) => {
              const options = availableByType[type] || [];
              const selected = getSelectedReport(type);
              const dropdownOptions = options.map((r) => ({
                label: `${r.fileName} · ${formatAge(r.createdAt)}${
                  r.sftpUploadedAt ? " · previously uploaded" : ""
                }`,
                value: r.id,
              }));
              return (
                <div
                  key={type}
                  style={{
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "flex-start",
                  }}
                >
                  <Checkbox
                    inputId={`sftp-${type}`}
                    checked={selectedTypes.has(type)}
                    disabled={options.length === 0}
                    onChange={(e) => toggleType(type, Boolean(e.checked))}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label htmlFor={`sftp-${type}`}>
                      <strong>{label}</strong>
                    </label>
                    {options.length > 0 && selected ? (
                      <div style={{ marginTop: "0.5rem" }}>
                        <Dropdown
                          value={selectedReportIds[type] || selected.id}
                          options={dropdownOptions}
                          onChange={(e) =>
                            setSelectedReportIds((prev) => ({
                              ...prev,
                              [type]: e.value as string,
                            }))
                          }
                          disabled={!selectedTypes.has(type)}
                          style={{ width: "100%", maxWidth: "48rem" }}
                          placeholder="Select a report"
                        />
                        <div
                          style={{
                            fontSize: "0.9rem",
                            marginTop: "0.35rem",
                            color: "var(--text-color-secondary)",
                          }}
                        >
                          {selected.fileSize} · {formatDate(selected.createdAt)}{" "}
                          ({formatAge(selected.createdAt)})
                          {selected.sftpUploadedAt ? (
                            <Tag
                              className="ml-2"
                              severity="info"
                              value={`Last uploaded ${formatDate(
                                selected.sftpUploadedAt
                              )}`}
                            />
                          ) : (
                            <Tag
                              className="ml-2"
                              severity="warning"
                              value="Not uploaded yet"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: "var(--text-color-secondary)" }}>
                        No available report
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <Button
                label="Accept & upload selected"
                icon="pi pi-upload"
                loading={uploadingReports}
                disabled={uploadingReports}
                onClick={handleUploadSelected}
              />
              <Button
                label="Refresh reports"
                icon="pi pi-refresh"
                text
                onClick={() => void loadAvailableReports()}
                disabled={loadingReports || uploadingReports}
              />
            </div>
          </div>
        )}
      </Card>

      <Card title="Manual file upload">
        <div
          style={{
            marginBottom: "0.75rem",
            padding: "0.75rem 1rem",
            borderLeft: "4px solid var(--orange-500, #f59e0b)",
            background: "var(--surface-ground, #f8f9fa)",
          }}
        >
          <strong>Direct upload.</strong> Choosing a file and confirming sends it
          straight to BMAT SFTP (no staging). Filename must match{" "}
          <code>
            Moonbug_DSR_&#123;GeneralEntertainment|MusicVideo|SoundRecording&#125;_Mapping_&#123;ddMonthyyyy&#125;.xlsx
          </code>{" "}
          (or <code>.csv</code>). Required spreadsheet columns are validated for
          that DSR type before upload.
        </div>
        <FileUpload
          mode="basic"
          name="file"
          customUpload
          uploadHandler={(e) => handleManualSelect(e)}
          chooseLabel={
            uploadingManual
              ? "Uploading…"
              : pendingManualFile
                ? "Choose another file"
                : "Choose DSR file"
          }
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          auto
          disabled={uploadingManual}
        />
        {uploadingManual && (
          <div style={{ marginTop: "0.75rem" }}>
            Validating columns and uploading to BMAT…
          </div>
        )}
      </Card>

      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <span>Remote files (/)</span>
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              text
              onClick={() => void loadFiles()}
              disabled={loadingFiles}
            />
          </div>
        }
        subTitle="Flat BMAT root listing"
      >
        {loadingFiles ? (
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        ) : (
          <DataTable
            value={files}
            emptyMessage="No files on the remote server"
            paginator
            rows={25}
            sortField="modifyTime"
            sortOrder={-1}
          >
            <Column field="name" header="Name" sortable />
            <Column
              field="type"
              header="Type"
              style={{ width: "8rem" }}
              sortable
            />
            <Column
              field="size"
              header="Size"
              sortable
              body={(row: SFTPRemoteFile) => formatBytes(row.size)}
              style={{ width: "8rem" }}
            />
            <Column
              field="modifyTime"
              header="Modified"
              sortable
              body={(row: SFTPRemoteFile) => formatDate(row.modifyTime)}
              style={{ width: "14rem" }}
            />
            <Column
              header="Actions"
              style={{ width: "8rem" }}
              body={(row: SFTPRemoteFile) =>
                row.type === "file" ? (
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    text
                    rounded
                    aria-label={`Delete ${row.name}`}
                    loading={deletingFile === row.name}
                    disabled={Boolean(deletingFile)}
                    onClick={() => handleDeleteRemoteFile(row)}
                  />
                ) : null
              }
            />
          </DataTable>
        )}
      </Card>
    </div>
  );
};

export default SftpBrowserRoute;
